/** Typed client for the guardrails API. */
import type {
  Analysis,
  ExtractionLevel,
  HazardId,
  PolicyName,
  Policy,
  Verdict,
} from "../shared/types";

export interface PromptsResponse {
  items: Analysis[];
  total: number;
}

export interface Stats {
  total: number;
  byVerdict: Record<Verdict, number>;
  byExtraction: Record<ExtractionLevel, number>;
  byHazard: Record<HazardId, number>;
  blockedShare: number;
  avgLatencyMs: number;
  medianLatencyMs: number;
  seedCount: number;
  userCount: number;
}

export interface AppConfig {
  llmModel: string;
  policies: Record<PolicyName, Policy>;
  extractionPolicy: { flag: number; throttle: number };
}

export type Completion =
  | { blocked: true; verdict: Verdict; action: string }
  | { blocked: false; text: string; model: string; latencyMs: number };

async function jfetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}

export interface ListParams {
  verdict?: Verdict;
  extraction?: ExtractionLevel;
  hazard?: HazardId;
  q?: string;
  limit?: number;
}

export function fetchPrompts(params: ListParams = {}): Promise<PromptsResponse> {
  const sp = new URLSearchParams();
  if (params.verdict) sp.set("verdict", params.verdict);
  if (params.extraction) sp.set("extraction", params.extraction);
  if (params.hazard) sp.set("hazard", params.hazard);
  if (params.q) sp.set("q", params.q);
  if (params.limit) sp.set("limit", String(params.limit));
  const qs = sp.toString();
  return jfetch<PromptsResponse>(`/api/prompts${qs ? `?${qs}` : ""}`);
}

export function fetchStats(): Promise<Stats> {
  return jfetch<Stats>("/api/stats");
}

export function fetchConfig(): Promise<AppConfig> {
  return jfetch<AppConfig>("/api/config");
}

export function analyzePrompt(text: string, policy: PolicyName = "strict"): Promise<Analysis> {
  return jfetch<Analysis>("/api/analyze", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ text, policy }),
  });
}

export function completePrompt(text: string): Promise<Completion> {
  return jfetch<Completion>("/api/complete", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ text }),
  });
}
